import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import {
  X, Loader2, Copy, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, XCircle,
  Circle, Network, KeyRound, TerminalSquare, Wifi, Info, RefreshCw, ShieldAlert, ShieldCheck,
} from "lucide-react";

const STEPS = [
  { id: "info", n: 1, title: "Informations drone" },
  { id: "wireguard", n: 2, title: "Configuration WireGuard" },
  { id: "ssh", n: 3, title: "Configuration SSH" },
  { id: "link", n: 4, title: "Test de liaison Raspberry" },
  { id: "agent", n: 5, title: "Agent SkyCore" },
  { id: "global", n: 6, title: "Test global" },
  { id: "summary", n: 7, title: "Résumé" },
];

const ST = {
  idle: { color: "#525252", label: "non configuré", Icon: Circle },
  pending: { color: "#3B82F6", label: "en attente", Icon: Loader2 },
  ok: { color: "#22C55E", label: "ok", Icon: CheckCircle2 },
  warning: { color: "#F59E0B", label: "warning", Icon: AlertTriangle },
  error: { color: "#EF4444", label: "erreur", Icon: XCircle },
};
const stMeta = (s) => ST[s] || ST.idle;
const stForGlobal = (g) => ({ connected: "ok", partial: "warning", not_connected: "error" }[g] || "unknown");

const CONFLICT_MSG = {
  duplicate_allowed_ip: "Doublon : la même IP WireGuard est associée à plusieurs pairs sur le serveur.",
  active_peer_without_allowed_ips: "Incohérence : un pair a un handshake récent mais aucun AllowedIPs.",
  allowed_ips_without_handshake: "Pair configuré (AllowedIPs présent) mais aucun handshake reçu pour l'instant.",
  peer_without_endpoint: "Pair présent mais sans endpoint actif (en attente du Raspberry).",
  db_key_differs_from_server: "La clé WireGuard enregistrée en base diffère de la clé active côté serveur.",
};
const conflictMsg = (c) => c.message || CONFLICT_MSG[c.type] || c.type;
const handshakeLabel = (s) => ({ recent: "handshake récent", waiting: "en attente de handshake", stale: "handshake ancien" }[s] || "—");

async function copyText(t) {
  try { await navigator.clipboard.writeText(t); toast.success("Copié"); return; } catch { /* fallback */ }
  try {
    const ta = document.createElement("textarea"); ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    toast.success("Copié");
  } catch { toast.error("Copie impossible"); }
}

function Help({ children }) {
  return (
    <div className="flex items-start gap-2 bg-[#3B82F6]/10 border border-[#3B82F6]/25 rounded-sm px-3 py-2 mb-3">
      <Info size={13} className="text-[#3B82F6] mt-0.5 shrink-0" />
      <div className="font-mono text-[10px] text-[#A3A3A3] leading-relaxed">{children}</div>
    </div>
  );
}

function Cmd({ code, testid }) {
  return (
    <div className="relative group mb-2">
      <pre data-testid={testid} className="bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2 pr-10 font-mono text-[10px] text-[#86EFAC] whitespace-pre-wrap break-all">{code}</pre>
      <button onClick={() => copyText(code)} title="Copier"
        className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10 text-[#A3A3A3]">
        <Copy size={11} />
      </button>
    </div>
  );
}

function Lbl({ label, children, hint }) {
  return (
    <label className="block mb-2.5">
      <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#525252]">{label}</span>
      {children}
      {hint && <span className="block font-mono text-[9px] text-[#525252] mt-0.5">{hint}</span>}
    </label>
  );
}

export default function DroneEnrollWizard({ onClose, onCreated, existingDrone }) {
  const navigate = useNavigate();
  const resuming = !!existingDrone;
  const [step, setStep] = useState(resuming ? 1 : 0);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ drone_id: "", name: "", profile: "", wireguard_ip: "", description: "" });
  const [created, setCreated] = useState(existingDrone || null);
  const [wgPubKey, setWgPubKey] = useState(existingDrone?.wireguard_public_key || "");
  const [sshUser, setSshUser] = useState(existingDrone?.ssh_user || "droneadmin");
  const [sshKey, setSshKey] = useState(null);
  const [sshTest, setSshTest] = useState(null);
  const [link, setLink] = useState(null);
  const [agentRes, setAgentRes] = useState(null);
  const [globalRes, setGlobalRes] = useState(null);
  const [status, setStatus] = useState({
    info: existingDrone ? "ok" : "idle",
    wireguard: existingDrone?.wireguard_peer_configured ? "ok" : "idle",
    ssh: "idle", link: "idle", agent: "idle", global: "idle",
  });
  const [statusMsg, setStatusMsg] = useState({});
  const [confirm, setConfirm] = useState(null); // {title,message,critical,needed,count,onYes}
  const [wgPeer, setWgPeer] = useState(null);    // peer-status (lecture seule)
  const wgRetryRef = useRef(false);
  const enrolledLocked = ["connected", "partial"].includes(existingDrone?.enrollment_status);

  const setSt = (k, state, msg) => { setStatus((s) => ({ ...s, [k]: state })); setStatusMsg((m) => ({ ...m, [k]: msg })); };

  // Étape 1 — création
  const createDrone = async () => {
    if (!form.drone_id.trim()) { toast.error("Drone ID requis"); return; }
    if (!form.wireguard_ip.trim()) { toast.error("IP WireGuard requise"); return; }
    setBusy("create");
    try {
      const { data } = await api.post("/drone-registry", {
        drone_id: form.drone_id.trim(), name: form.name.trim(), profile: form.profile.trim(),
        wireguard_ip: form.wireguard_ip.trim(), description: form.description.trim(),
      });
      setCreated(data.drone);
      setSt("info", "ok", "Drone créé.");
      toast.success("Drone créé");
      onCreated?.();
      setStep(1);
    } catch (e) { setSt("info", "error", apiErr(e)); toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  // Étape 2 — état du pair WireGuard côté serveur (lecture seule, anti-conflit)
  const loadWgPeer = useCallback(async () => {
    if (!created?.wireguard_ip) return;
    setBusy("wgpeer");
    try {
      const { data } = await api.get("/infrastructure/wireguard/peer-status", {
        params: { ip: created.wireguard_ip, drone_id: created.drone_id, public_key: wgPubKey.trim() || undefined },
      });
      setWgPeer(data);
    } catch { /* silencieux : la config reste possible */ }
    finally { setBusy(""); }
  }, [created, wgPubKey]);

  useEffect(() => { if (step === 1 && created?.wireguard_ip && wgPeer === null) loadWgPeer(); }, [step, created, wgPeer, loadWgPeer]);

  // Étape 2 — WireGuard peer (avec garde-fou anti-conflit)
  const submitWg = async (force = false) => {
    setBusy("wg"); setSt("wireguard", "pending", "Configuration en cours…");
    try {
      const { data } = await api.post("/infrastructure/wireguard/peer", {
        drone_id: created.drone_id, public_key: wgPubKey.trim(),
        wireguard_ip: created.wireguard_ip, description: created.drone_id, force,
      });
      if (data.ok && data.already_configured) {
        setSt("wireguard", "ok", "Pair déjà configuré (clé identique) — aucune modification.");
        setCreated((c) => ({ ...c, wireguard_peer_configured: true }));
        toast.success("Pair WireGuard déjà configuré"); loadWgPeer();
      } else if (data.ok) {
        setSt("wireguard", "ok", force ? "Pair WireGuard remplacé (confirmé)." : "Pair WireGuard ajouté au serveur.");
        setCreated((c) => ({ ...c, wireguard_peer_configured: true }));
        toast.success("Pair WireGuard configuré"); loadWgPeer();
      } else if (data.conflict) {
        setWgPeer((p) => ({ ...(p || {}), peer_exists: true, existing_public_key: data.existing_peer?.public_key,
          endpoint: data.existing_peer?.endpoint, latest_handshake_seconds_ago: data.existing_peer?.latest_handshake_seconds_ago,
          has_recent_handshake: data.existing_peer?.has_recent_handshake, conflict_active: true }));
        setSt("wireguard", "error", data.message);
        toast.error("Conflit WireGuard — remplacement à confirmer");
      } else {
        const msg = data.not_deployed ? data.message : (data.error || "Échec de la configuration");
        setSt("wireguard", data.not_deployed ? "warning" : "error", msg);
        toast.error(msg);
      }
    } catch (e) { setSt("wireguard", "error", apiErr(e)); toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  const askWg = () => {
    if (!wgPubKey.trim()) { toast.error("Clé publique WireGuard requise"); return; }
    const entered = wgPubKey.trim();
    const existKey = wgPeer?.existing_public_key;
    if (existKey && existKey === entered) { submitWg(false); return; } // idempotent
    if (existKey && existKey !== entered) {
      setConfirm({
        title: "Conflit WireGuard — remplacer la clé existante",
        message: `L'IP ${created.wireguard_ip}/32 est DÉJÀ associée à une clé publique différente (${existKey.slice(0, 14)}…). Le remplacement écrase la configuration existante. Confirmez DEUX fois pour appliquer.`,
        critical: true, needed: 2, count: 0, onYes: () => submitWg(true),
      });
      return;
    }
    if (enrolledLocked) {
      setConfirm({
        title: "Drone déjà enrôlé — ré-enrôlement WireGuard",
        message: `${created.drone_id} est déjà ${existingDrone?.enrollment_status === "connected" ? "connecté" : "partiellement connecté"}. Évitez de régénérer la clé. Confirmez DEUX fois pour forcer un nouveau pair.`,
        critical: true, needed: 2, count: 0, onYes: () => submitWg(true),
      });
      return;
    }
    setConfirm({
      title: "Configurer le pair WireGuard",
      message: `Ajouter le pair WireGuard de ${created.drone_id} (${created.wireguard_ip}/32) sur le serveur Gateway ?`,
      critical: false, needed: 1, count: 0, onYes: () => submitWg(false),
    });
  };

  // Étape 3 — clé SSH publique de l'Infra API
  const loadSshKey = useCallback(async () => {
    setBusy("sshkey");
    try {
      const { data } = await api.get("/infrastructure/ssh/public-key");
      const inner = data.data || {};
      if (data.ok && inner.public_key) {
        setSshKey({ ok: true, public_key: inner.public_key, ssh_user: inner.ssh_user, path: inner.path });
      } else if (data.not_deployed) {
        setSshKey({ ok: false, not_deployed: true, message: data.message, path: data.path });
      } else {
        const st = data.status; const et = data.error_type;
        let msg = data.error || inner.error || "Clé publique indisponible";
        if (st === 401 || st === 403 || et === "auth_invalid" || et === "auth_forbidden") msg = "Erreur d'authentification Infra API (401/403).";
        else if (st === 404 || et === "not_found") msg = "Clé publique absente côté Infra API (404).";
        else if (et === "timeout") msg = "Infra API injoignable (timeout).";
        setSshKey({ ok: false, error: msg });
      }
    } catch (e) { setSshKey({ ok: false, error: apiErr(e) }); }
    finally { setBusy(""); }
  }, []);

  useEffect(() => { if (step === 2 && !sshKey) loadSshKey(); }, [step, sshKey, loadSshKey]);

  // Étape 3 — test SSH immédiat (retour rapide après installation de la clé)
  const testSshStep3 = async () => {
    setBusy("sshtest"); setSshTest({ pending: true });
    try {
      const { data } = await api.post("/infrastructure/raspberries/test-connection", {
        drone_id: created.drone_id, wireguard_ip: created.wireguard_ip, ssh_user: sshUser,
      });
      const payload = data.data || {};
      if (data.not_deployed) {
        setSshTest({ degraded: true, message: data.message });
      } else if (data.ok && payload.ssh === "ok") {
        setSshTest({ ok: true, hostname: payload.hostname, user: payload.user, os: payload.os });
        setSt("ssh", "ok", `SSH OK — ${payload.hostname || "hôte joint"} (${payload.user || sshUser})`);
        toast.success("SSH opérationnel");
      } else {
        const msg = payload.error || data.error || "Connexion SSH impossible";
        setSshTest({ ok: false, error: msg });
        setSt("ssh", "error", msg);
      }
    } catch (e) { setSshTest({ ok: false, error: apiErr(e) }); setSt("ssh", "error", apiErr(e)); }
    finally { setBusy(""); }
  };

  // Étape 4 — test de liaison
  const testLink = async () => {
    setBusy("link"); setSt("link", "pending", "Test de connexion en cours…");
    try {
      const { data } = await api.post("/infrastructure/raspberries/test-connection", {
        drone_id: created.drone_id, wireguard_ip: created.wireguard_ip, ssh_user: sshUser,
      });
      const payload = data.data || {};
      if (data.not_deployed) {
        setLink({ degraded: true, message: data.message }); setSt("link", "warning", data.message);
      } else if (data.ok && payload.ssh === "ok") {
        setLink(payload); setSt("link", "ok", `SSH OK — ${payload.hostname || "hôte joint"}`);
      } else {
        const msg = payload.error || data.error || "Connexion impossible";
        setLink({ ...payload, error: msg }); setSt("link", "error", msg);
      }
    } catch (e) { setLink({ error: apiErr(e) }); setSt("link", "error", apiErr(e)); }
    finally { setBusy(""); }
  };

  // Étape 5 — agent
  const provisionAgent = async () => {
    if (!created?.id) return;
    setBusy("agent"); setSt("agent", "pending", "Provisioning de l'agent en cours…");
    try {
      const { data } = await api.post(`/infrastructure/raspberries/${created.id}/provision-agent`, { restart: true });
      const inner = data.data || {};
      if (data.ok && inner.ok !== false) { setAgentRes(inner); setSt("agent", "ok", "Agent SkyCore installé / mis à jour."); toast.success("Agent provisionné"); }
      else if (data.not_deployed) setSt("agent", "warning", data.message);
      else { const m = inner.error || data.error || "Échec du provisioning"; setAgentRes(inner); setSt("agent", "error", m); toast.error(m); }
    } catch (e) { setSt("agent", "error", apiErr(e)); toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  const verifyAgent = async () => {
    if (!created?.id) return;
    setBusy("verify"); setSt("agent", "pending", "Vérification de l'agent…");
    try {
      const { data } = await api.get(`/infrastructure/raspberries/${created.id}/status`);
      if (data.ok) { const d = data.data || {}; setAgentRes(d); setSt("agent", d.service === "active" ? "ok" : "warning", `Service : ${d.service || "?"} · v${d.software_version || "?"}`); }
      else if (data.not_deployed) setSt("agent", "warning", data.message);
      else setSt("agent", "error", data.error || "Vérification impossible");
    } catch (e) { setSt("agent", "error", apiErr(e)); }
    finally { setBusy(""); }
  };

  // Étape 6 — test global
  const runGlobalTest = async () => {
    if (!created?.id) return;
    setBusy("global"); setSt("global", "pending", "Test global en cours…");
    try {
      const { data } = await api.post(`/infrastructure/enrollment/${created.id}/global-test`);
      setGlobalRes(data);
      const map = { connected: "ok", partial: "warning", not_connected: "error" };
      setSt("global", map[data.status] || "warning", data.status_label);
      onCreated?.();
      // Cas PersistentKeepalive : pair présent mais handshake en attente -> re-test auto à 30 s (une fois).
      if (data.wireguard?.handshake === "waiting" && !wgRetryRef.current) {
        wgRetryRef.current = true;
        setSt("global", "pending", "WireGuard en attente de handshake du Raspberry — nouvelle tentative dans 30 s…");
        setTimeout(() => { wgRetryRef.current = false; runGlobalTest(); }, 30000);
      }
    } catch (e) { setSt("global", "error", apiErr(e)); toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  // Remontée d'un conflit WireGuard vers l'Assistant IA (lecture / diagnostic uniquement)
  const escalateConflictToAI = () => {
    const list = (globalRes?.conflicts || []).map((c) => `- ${conflictMsg(c)}${c.allowed_ip ? ` (${c.allowed_ip})` : ""} [${c.severity}]`).join("\n");
    const prompt = `Conflit WireGuard détecté lors du test global du drone ${created.drone_id} (IP ${created.wireguard_ip}).\nConflits remontés :\n${list}\n\nUtilise check_wireguard_conflicts (lecture seule) pour analyser, explique précisément le conflit et propose un diagnostic. Ne corrige RIEN automatiquement : toute action (remplacement de clé, suppression de pair, ré-enrôlement) doit rester soumise à validation admin avec double confirmation.`;
    onClose?.();
    navigate("/assistant", { state: { prefill: prompt, context: "infrastructure", autoSend: true, droneId: created.drone_id } });
  };

  // Bonus — script Raspberry complet
  const copyAllCommands = async () => {
    if (!created?.id) { toast.error("Créez d'abord le drone (étape 1)"); return; }
    try { const { data } = await api.get(`/infrastructure/enrollment/${created.id}/setup-script`); await copyText(data.script); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const cur = STEPS[step];
  const infraPubKey = sshKey?.public_key || "<CLE_PUBLIQUE_INFRA_API>";
  const sshCmd = sshUser === "droneadmin"
    ? `mkdir -p ~/.ssh && chmod 700 ~/.ssh\necho "${infraPubKey}" >> ~/.ssh/authorized_keys\nchmod 600 ~/.ssh/authorized_keys`
    : `sudo -i\nmkdir -p /root/.ssh && chmod 700 /root/.ssh\necho "${infraPubKey}" >> /root/.ssh/authorized_keys\nchmod 600 /root/.ssh/authorized_keys && chown -R root:root /root/.ssh\ncat > /etc/ssh/sshd_config.d/99-skycore-root.conf <<'EOF'\nPermitRootLogin prohibit-password\nPubkeyAuthentication yes\nEOF\n/usr/sbin/sshd -t && systemctl restart ssh`;

  const onConfirmYes = () => {
    if (!confirm) return;
    if (confirm.count + 1 < confirm.needed) {
      setConfirm({ ...confirm, count: confirm.count + 1, message: "Action critique — confirmez une seconde fois pour appliquer." });
      return;
    }
    const fn = confirm.onYes; setConfirm(null); fn?.();
  };

  return (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center p-3 bg-black/75 backdrop-blur-sm" data-testid="enroll-wizard">
      <div className="bg-[#0F0F0F] border border-white/15 rounded-sm w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl">
        {/* Header + stepper */}
        <div className="px-5 py-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <Network size={18} className="text-[#3B82F6]" />
            <div>
              <div className="font-chivo text-lg font-black tracking-tighter uppercase leading-none">Assistant de connexion drone</div>
              <div className="font-mono text-[10px] text-[#A3A3A3] mt-0.5">{created ? created.drone_id : "Nouveau drone"} · admin</div>
            </div>
            <button data-testid="enroll-close" onClick={onClose} className="ml-auto w-7 h-7 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10"><X size={14} /></button>
          </div>
          <div className="flex items-center gap-1 mt-4 overflow-x-auto">
            {STEPS.map((s, i) => {
              const sk = status[s.id];
              const m = stMeta(sk);
              const active = i === step;
              return (
                <div key={s.id} className="flex items-center shrink-0">
                  <button onClick={() => (created ? setStep(i) : null)}
                    data-testid={`enroll-step-tab-${s.id}`}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-sm transition-colors ${active ? "bg-white/10" : ""} ${created ? "cursor-pointer" : "cursor-default"}`}>
                    <span className="w-5 h-5 flex items-center justify-center rounded-full font-mono text-[10px] font-bold border"
                      style={{ borderColor: m.color, color: active ? "#fff" : m.color, background: active ? m.color : "transparent" }}>{s.n}</span>
                    <span className={`font-mono text-[9px] uppercase tracking-wider hidden md:inline ${active ? "text-white" : "text-[#737373]"}`}>{s.title}</span>
                  </button>
                  {i < STEPS.length - 1 && <ChevronRight size={12} className="text-[#333] mx-0.5" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5" data-testid={`enroll-body-${cur.id}`}>
          {/* current step status banner */}
          {status[cur.id] && status[cur.id] !== "idle" && statusMsg[cur.id] && (
            <div data-testid={`enroll-status-${cur.id}`} className="flex items-center gap-2 mb-3 px-3 py-2 rounded-sm border"
              style={{ borderColor: `${stMeta(status[cur.id]).color}55`, background: `${stMeta(status[cur.id]).color}12` }}>
              {(() => { const I = stMeta(status[cur.id]).Icon; return <I size={14} className={status[cur.id] === "pending" ? "animate-spin" : ""} style={{ color: stMeta(status[cur.id]).color }} />; })()}
              <span className="font-mono text-[11px]" style={{ color: stMeta(status[cur.id]).color }}>{statusMsg[cur.id]}</span>
            </div>
          )}

          {/* STEP 1 */}
          {cur.id === "info" && (
            <div>
              <Help>L'IP WireGuard est l'adresse VPN privée qui permettra au serveur SkyCore de contacter le Raspberry du drone à distance. Exemple : 10.50.0.11 (réseau 10.50.0.0/24).</Help>
              {created ? (
                <div className="font-mono text-[11px] text-[#22C55E]">Drone « {created.drone_id} » déjà créé. Passez à l'étape suivante.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  <Lbl label="Drone ID *"><input data-testid="enroll-drone-id" value={form.drone_id} onChange={(e) => setForm({ ...form, drone_id: e.target.value })} placeholder="DRONE-V2" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-2 font-mono text-[12px] text-white outline-none" /></Lbl>
                  <Lbl label="Nom affiché"><input data-testid="enroll-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Drone surveillance V2" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-2 font-mono text-[12px] text-white outline-none" /></Lbl>
                  <Lbl label="Type / profil"><input data-testid="enroll-profile" value={form.profile} onChange={(e) => setForm({ ...form, profile: e.target.value })} placeholder="surveillance" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-2 font-mono text-[12px] text-white outline-none" /></Lbl>
                  <Lbl label="IP WireGuard *" hint="Doit appartenir à 10.50.0.0/24"><input data-testid="enroll-wg-ip" value={form.wireguard_ip} onChange={(e) => setForm({ ...form, wireguard_ip: e.target.value })} placeholder="10.50.0.11" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-2 font-mono text-[12px] text-white outline-none" /></Lbl>
                  <div className="sm:col-span-2">
                    <Lbl label="Description (optionnel)"><input data-testid="enroll-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-2 font-mono text-[12px] text-white outline-none" /></Lbl>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 */}
          {cur.id === "wireguard" && (
            <div>
              <Help>WireGuard crée un tunnel privé entre le serveur SkyCore et le Raspberry. <b className="text-[#F59E0B]">Collez uniquement la clé PUBLIQUE du Raspberry. Ne collez jamais la clé privée</b> — elle doit rester sur le Raspberry.</Help>

              {enrolledLocked && (
                <div data-testid="enroll-wg-diag-mode" className="flex items-start gap-2 mb-3 px-3 py-2 rounded-sm border border-[#22C55E]/30 bg-[#22C55E]/10">
                  <ShieldCheck size={14} className="text-[#22C55E] mt-0.5 shrink-0" />
                  <div className="font-mono text-[10px] text-[#A3A3A3] leading-relaxed">
                    Drone déjà enrôlé ({existingDrone?.enrollment_status === "connected" ? "connecté" : "partiellement connecté"}) — <b className="text-[#22C55E]">mode vérification / diagnostic</b>. Aucun ré-enrôlement WireGuard automatique. Ne régénérez pas la clé sauf nécessité.
                  </div>
                </div>
              )}

              {wgPeer?.peer_exists && (
                <div data-testid="enroll-wg-existing" className="mb-3 rounded-sm border p-3"
                  style={{ borderColor: wgPeer.conflict_active ? "#EF444455" : "#3B82F655", background: wgPeer.conflict_active ? "#EF44441A" : "#3B82F614" }}>
                  <div className="flex items-center gap-2 mb-2">
                    {wgPeer.conflict_active ? <ShieldAlert size={14} className="text-[#EF4444]" /> : <Info size={14} className="text-[#3B82F6]" />}
                    <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: wgPeer.conflict_active ? "#EF4444" : "#3B82F6" }}>
                      Pair WireGuard déjà présent pour {wgPeer.ip}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5">
                    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-1.5">
                      <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#525252]">Clé publique active (serveur)</div>
                      <div className="font-mono text-[10px] text-[#86EFAC] break-all">{wgPeer.existing_public_key || "—"}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-1.5">
                        <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#525252]">Handshake</div>
                        <div className="font-mono text-[10px]" style={{ color: wgPeer.has_recent_handshake ? "#22C55E" : "#F59E0B" }}>
                          {wgPeer.has_recent_handshake ? `il y a ${wgPeer.latest_handshake_seconds_ago}s` : (wgPeer.latest_handshake_seconds_ago == null ? "en attente" : `il y a ${wgPeer.latest_handshake_seconds_ago}s`)}
                        </div>
                      </div>
                      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-1.5">
                        <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#525252]">Endpoint</div>
                        <div className="font-mono text-[10px] text-white break-all">{wgPeer.endpoint || "—"}</div>
                      </div>
                    </div>
                    {wgPeer.key_matches === false && (
                      <div className="font-mono text-[10px] text-[#EF4444]">⚠ La clé saisie diffère de la clé active — un remplacement nécessitera une double confirmation.</div>
                    )}
                  </div>
                </div>
              )}

              <Lbl label="Clé publique WireGuard du Raspberry">
                <textarea data-testid="enroll-wg-pubkey" value={wgPubKey} onChange={(e) => setWgPubKey(e.target.value)} rows={2}
                  placeholder="ex: kQ9...exemple...=" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-2 font-mono text-[11px] text-white outline-none break-all" />
              </Lbl>
              <div className="font-mono text-[10px] text-[#525252] mb-1">Allowed IPs : <span className="text-[#A3A3A3]">{created?.wireguard_ip}/32</span></div>
              <div className="font-mono text-[10px] text-[#A3A3A3] mt-3 mb-1">Commandes à exécuter sur le Raspberry pour obtenir la clé publique :</div>
              <Cmd testid="enroll-wg-cmd1" code={"wg genkey | tee privatekey | wg pubkey > publickey\ncat publickey"} />
              <Cmd testid="enroll-wg-cmd2" code={"# ou si WireGuard est déjà configuré :\nsudo cat /etc/wireguard/publickey"} />
              <button data-testid="enroll-wg-configure" onClick={askWg} disabled={busy === "wg"}
                className="mt-2 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-40">
                {busy === "wg" ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Configurer le pair WireGuard sur le serveur
              </button>
            </div>
          )}

          {/* STEP 3 */}
          {cur.id === "ssh" && (
            <div>
              <Help>SSH permet à SkyCore d'installer et maintenir l'agent drone sur le Raspberry. La clé PUBLIQUE du serveur Infra API ci-dessous doit être ajoutée au Raspberry.</Help>
              <Lbl label="Utilisateur SSH">
                <select data-testid="enroll-ssh-user" value={sshUser} onChange={(e) => setSshUser(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-2 font-mono text-[12px] text-white outline-none">
                  <option value="droneadmin">droneadmin (recommandé, sudo contrôlé)</option>
                  <option value="root">root (avancé, clé SSH uniquement)</option>
                </select>
              </Lbl>
              <div className="flex items-center gap-2 mb-1">
                <div className="font-mono text-[10px] text-[#A3A3A3]">Clé publique SSH de l'Infra API (à installer sur le Raspberry) :</div>
                <button data-testid="enroll-ssh-refresh" onClick={loadSshKey} disabled={busy === "sshkey"} title="Rafraîchir"
                  className="ml-auto w-6 h-6 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10 disabled:opacity-40">
                  <RefreshCw size={11} className={busy === "sshkey" ? "animate-spin" : ""} />
                </button>
              </div>
              {busy === "sshkey" ? (
                <div className="font-mono text-[10px] text-[#525252] py-2">Chargement…</div>
              ) : sshKey?.ok ? (
                <Cmd testid="enroll-ssh-pubkey" code={sshKey.public_key} />
              ) : sshKey?.not_deployed ? (
                <div data-testid="enroll-ssh-degraded" className="flex items-center gap-2 font-mono text-[10px] text-[#F59E0B] border border-[#F59E0B]/30 bg-[#F59E0B]/10 rounded-sm px-3 py-2 mb-2">
                  <AlertTriangle size={13} /> {sshKey.message} (chemin : {sshKey.path})
                </div>
              ) : (
                <div data-testid="enroll-ssh-error" className="flex items-center gap-2 font-mono text-[10px] text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/10 rounded-sm px-3 py-2 mb-2">
                  <AlertTriangle size={13} /> {sshKey?.error || "Clé indisponible"}
                </div>
              )}
              <div className="font-mono text-[10px] text-[#A3A3A3] mt-3 mb-1">Commandes sur le Raspberry ({sshUser}) :</div>
              <Cmd testid="enroll-ssh-cmd" code={sshCmd} />
              <button data-testid="enroll-copy-script" onClick={copyAllCommands} disabled={!created}
                className="mt-2 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-[#A855F7]/50 text-[#A855F7] rounded-sm px-3 py-2 hover:bg-[#A855F7]/15 disabled:opacity-40">
                <Copy size={13} /> Copier toutes les commandes Raspberry (setup.sh)
              </button>

              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="font-mono text-[10px] text-[#A3A3A3] mb-2">Une fois la clé installée sur le Raspberry, vérifiez immédiatement la connexion SSH :</div>
                <button data-testid="enroll-ssh-test" onClick={testSshStep3} disabled={busy === "sshtest" || !created}
                  className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-40">
                  {busy === "sshtest" ? <Loader2 size={13} className="animate-spin" /> : <Wifi size={13} />} Tester SSH
                </button>
                {sshTest && !sshTest.pending && (
                  <div className="mt-2">
                    {sshTest.ok ? (
                      <div data-testid="enroll-ssh-test-ok" className="flex items-center gap-2 font-mono text-[10px] text-[#22C55E] border border-[#22C55E]/30 bg-[#22C55E]/10 rounded-sm px-3 py-2">
                        <CheckCircle2 size={13} /> SSH OK — {sshTest.hostname || "hôte joint"} · {sshTest.user || sshUser}{sshTest.os ? ` · ${sshTest.os}` : ""}
                      </div>
                    ) : sshTest.degraded ? (
                      <div data-testid="enroll-ssh-test-degraded" className="flex items-center gap-2 font-mono text-[10px] text-[#F59E0B] border border-[#F59E0B]/30 bg-[#F59E0B]/10 rounded-sm px-3 py-2">
                        <AlertTriangle size={13} /> {sshTest.message}
                      </div>
                    ) : (
                      <div data-testid="enroll-ssh-test-error" className="font-mono text-[10px] text-[#A3A3A3] border border-[#EF4444]/30 bg-[#EF4444]/10 rounded-sm px-3 py-2">
                        <div className="text-[#EF4444] mb-1 flex items-center gap-1.5"><XCircle size={12} /> {sshTest.error}</div>
                        Causes possibles : clé SSH pas encore installée · mauvais utilisateur ({sshUser}) · port SSH fermé · WireGuard non joignable.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {cur.id === "link" && (
            <div>
              <Help>Ce test vérifie que le serveur peut joindre le Raspberry via WireGuard et s'y connecter en SSH (hostname, OS, uptime, utilisateur).</Help>
              <button data-testid="enroll-test-link" onClick={testLink} disabled={busy === "link"}
                className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-40 mb-3">
                {busy === "link" ? <Loader2 size={13} className="animate-spin" /> : <Wifi size={13} />} Tester la connexion Raspberry
              </button>
              {link && !link.degraded && !link.error && (
                <div data-testid="enroll-link-result" className="grid grid-cols-2 gap-2">
                  {[["Réseau", link.network], ["SSH", link.ssh], ["Hostname", link.hostname], ["OS", link.os], ["Uptime", link.uptime], ["Utilisateur", link.user], ["Service agent", link.service], ["Version", link.software_version]].map(([k, v]) => (
                    <div key={k} className="bg-[#141414] border border-white/10 rounded-sm px-2.5 py-1.5">
                      <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#525252]">{k}</div>
                      <div className="font-mono text-[11px] text-white break-all">{v || "—"}</div>
                    </div>
                  ))}
                </div>
              )}
              {link?.error && (
                <div className="font-mono text-[10px] text-[#A3A3A3] border border-[#EF4444]/30 bg-[#EF4444]/10 rounded-sm px-3 py-2">
                  <div className="text-[#EF4444] mb-1">{link.error}</div>
                  Causes possibles : WireGuard non joignable · port SSH fermé · clé SSH non installée · mauvais utilisateur · permission refusée · Raspberry hors ligne.
                </div>
              )}
              {link?.degraded && (
                <div className="font-mono text-[10px] text-[#F59E0B] border border-[#F59E0B]/30 bg-[#F59E0B]/10 rounded-sm px-3 py-2">{link.message}</div>
              )}
            </div>
          )}

          {/* STEP 5 — Agent */}
          {cur.id === "agent" && (
            <div>
              <Help>L'agent SkyCore connecte le Raspberry à la Gateway, transmet la télémétrie et reçoit les commandes. Le provisioning s'effectue depuis le serveur via SSH.</Help>
              <div className="flex flex-wrap gap-2 mb-3">
                <button data-testid="enroll-provision-agent" onClick={provisionAgent} disabled={busy === "agent"}
                  className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider bg-[#A855F7] rounded-sm px-4 py-2 hover:bg-[#A855F7]/85 disabled:opacity-40">
                  {busy === "agent" ? <Loader2 size={13} className="animate-spin" /> : <TerminalSquare size={13} />} Installer / mettre à jour l'agent
                </button>
                <button data-testid="enroll-verify-agent" onClick={verifyAgent} disabled={busy === "verify"}
                  className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10 disabled:opacity-40">
                  {busy === "verify" ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Vérifier l'agent existant
                </button>
              </div>
              {agentRes && (
                <div data-testid="enroll-agent-result" className="grid grid-cols-2 gap-2">
                  {[["Service", agentRes.service], ["Version", agentRes.software_version], ["Hostname", agentRes.hostname]].filter(([, v]) => v !== undefined).map(([k, v]) => (
                    <div key={k} className="bg-[#141414] border border-white/10 rounded-sm px-2.5 py-1.5">
                      <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#525252]">{k}</div>
                      <div className="font-mono text-[11px] text-white break-all">{v || "—"}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 6 — Test global */}
          {cur.id === "global" && (
            <div>
              <Help>Validation complète : serveur/WireGuard, liaison Raspberry, agent SkyCore, Gateway et MAVLink/Pixhawk. Lecture seule, aucune action exécutée.</Help>
              <button data-testid="enroll-run-global" onClick={runGlobalTest} disabled={busy === "global"}
                className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-40 mb-3">
                {busy === "global" ? <Loader2 size={13} className="animate-spin" /> : <Wifi size={13} />} Lancer le test global de connexion
              </button>
              {globalRes && (
                <div data-testid="enroll-global-result" className="space-y-2">
                  {globalRes.conflicts?.length > 0 && (
                    <div data-testid="enroll-wg-conflicts" className="rounded-sm border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 py-2 mb-1">
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldAlert size={14} className="text-[#EF4444]" />
                        <span className="font-mono text-[10px] uppercase tracking-wider text-[#EF4444]">Conflit WireGuard détecté</span>
                      </div>
                      {globalRes.conflicts.map((c, i) => (
                        <div key={i} className="font-mono text-[10px] text-[#A3A3A3] leading-relaxed pl-5">
                          • <span style={{ color: c.severity === "critical" ? "#EF4444" : "#F59E0B" }}>{conflictMsg(c)}</span>
                          {c.allowed_ip ? ` (${c.allowed_ip})` : ""}
                        </div>
                      ))}
                      <div className="font-mono text-[9px] text-[#525252] pl-5 mt-1">Vérifiez la clé publique WireGuard et la configuration serveur avant de continuer.</div>
                      <button data-testid="enroll-wg-conflict-ai" onClick={escalateConflictToAI}
                        className="mt-2 ml-5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-[#A855F7]/50 text-[#A855F7] rounded-sm px-3 py-1.5 hover:bg-[#A855F7]/15">
                        <ShieldAlert size={12} /> Analyser le conflit avec l'Assistant IA (diagnostic)
                      </button>
                    </div>
                  )}
                  {[
                    ["WireGuard", `pair ${globalRes.wireguard.configured ? "configuré" : "non configuré"}${globalRes.wireguard.peer_present ? " · présent serveur" : ""}${globalRes.wireguard.handshake ? ` · ${handshakeLabel(globalRes.wireguard.handshake)}` : ""}${globalRes.wireguard.waiting_handshake ? " · keepalive en attente" : ""} · ${globalRes.wireguard.ip}`],
                    ["SSH", globalRes.ssh.reachable === true ? `OK (${globalRes.ssh.user}, clé)` : globalRes.ssh.reachable === false ? "non joignable" : "indéterminé"],
                    ["Agent", `${globalRes.agent.running ? "actif" : globalRes.agent.running === false ? "arrêté" : "indéterminé"}${globalRes.agent.version ? ` · v${globalRes.agent.version}` : ""}`],
                    ["Gateway", `${globalRes.gateway.websocket || "—"} · heartbeat ${globalRes.gateway.last_heartbeat || "—"}`],
                    ["MAVLink", globalRes.mavlink.message],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-start gap-2 bg-[#141414] border border-white/10 rounded-sm px-2.5 py-1.5">
                      <div className="font-mono text-[9px] uppercase tracking-wider text-[#525252] w-20 shrink-0 pt-0.5">{k}</div>
                      <div className="font-mono text-[11px] text-[#E5E5E5] break-all">{v}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 7 — Résumé */}
          {cur.id === "summary" && (
            <div>
              {globalRes ? (
                <>
                  <div data-testid="enroll-summary-status" className="rounded-sm border p-4 mb-3 flex items-center gap-3"
                    style={{ borderColor: `${stMeta(stForGlobal(globalRes.status)).color}55`, background: `${stMeta(stForGlobal(globalRes.status)).color}12` }}>
                    {(() => { const I = stMeta(stForGlobal(globalRes.status)).Icon; return <I size={24} style={{ color: stMeta(stForGlobal(globalRes.status)).color }} />; })()}
                    <div>
                      <div className="font-chivo text-lg font-black tracking-tighter uppercase" style={{ color: stMeta(stForGlobal(globalRes.status)).color }}>
                        {globalRes.status === "connected" ? "Connecté" : globalRes.status === "partial" ? "Partiellement connecté" : "Non connecté"}
                      </div>
                      <div className="font-mono text-[10px] text-[#A3A3A3]">{globalRes.status_label}</div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {globalRes.checks.map((c) => (
                      <div key={c.label} className="flex items-center gap-2 font-mono text-[11px]">
                        {(() => { const I = stMeta(c.status).Icon; return <I size={13} style={{ color: stMeta(c.status).color }} />; })()}
                        <span style={{ color: stMeta(c.status).color }}>{c.label}</span>
                      </div>
                    ))}
                  </div>
                  <button data-testid="enroll-rerun-global" onClick={() => { setStep(5); }}
                    className="mt-4 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10">
                    <Wifi size={13} /> Relancer le test global
                  </button>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center text-center gap-2 py-10 text-[#525252]">
                  <Wifi size={26} />
                  <div className="font-mono text-[11px]">Lancez le « Test global » (étape 6) pour obtenir le statut final.</div>
                  <button data-testid="enroll-goto-global" onClick={() => setStep(5)} className="mt-1 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10">Aller au test global</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-white/10 shrink-0">
          <button data-testid="enroll-prev" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
            className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10 disabled:opacity-30">
            <ChevronLeft size={13} /> Précédent
          </button>
          {step === 0 && !created ? (
            <button data-testid="enroll-create-submit" onClick={createDrone} disabled={busy === "create"}
              className="ml-auto flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-40">
              {busy === "create" ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Créer le drone
            </button>
          ) : (
            <button data-testid="enroll-next" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} disabled={step >= STEPS.length - 1}
              className="ml-auto flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-40">
              Continuer <ChevronRight size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Confirm overlay */}
      {confirm && (
        <div className="fixed inset-0 z-[1700] flex items-center justify-center bg-black/70 p-4" data-testid="enroll-confirm" onClick={() => setConfirm(null)}>
          <div className="bg-[#141414] border border-white/15 rounded-sm max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={15} style={{ color: confirm.critical ? "#EF4444" : "#F59E0B" }} />
              <span className="font-chivo font-bold tracking-tight uppercase text-sm">{confirm.title}</span>
            </div>
            <div className="font-mono text-[11px] text-[#A3A3A3] mb-3">{confirm.message}</div>
            <div className="flex items-center justify-end gap-2">
              <button data-testid="enroll-confirm-cancel" onClick={() => setConfirm(null)} className="font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10">Annuler</button>
              <button data-testid="enroll-confirm-yes" onClick={onConfirmYes}
                className={`font-mono text-[10px] uppercase tracking-wider rounded-sm px-4 py-2 ${confirm.critical ? "bg-[#EF4444] hover:bg-[#EF4444]/85" : "bg-[#3B82F6] hover:bg-[#3B82F6]/85"}`}>
                {confirm.needed > 1 && confirm.count >= 1 ? "Confirmer (final)" : "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
